/**
 * Вариант посевного плана: строки плана, покрытие техкартами, сводка.
 *
 * Экраны собраны по УТВЕРЖДЁННЫМ founder макетам (18.08):
 *   `docs/superpowers/prototypes/annual-planning/v2-04-sowing-plan.html`
 *   `docs/superpowers/prototypes/annual-planning/v2-14-variant-summary.html`
 *   (ветка `docs/wireframes-season-planning-v2`, скриншоты рядом).
 *
 * Один запрос на два экрана — сознательно: «затраты по назначенному» в шапке
 * назначения обязаны совпасть до рубля со «сводкой», иначе агроном перестанет
 * верить обеим цифрам. Бэк отдаёт один расчёт (`GET .../summary`), экраны
 * берут из него разные срезы.
 *
 * Язык — агронома: «строка плана», «культура по предшественнику», «вариант
 * техкарты», «без техкарты». Модельные слова (scenario, assignment, state)
 * наружу не отдаём.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { VariantTargets } from '@/lib/planning/budgetTargets';
import { planVariantKeys, techCardKeys } from '@/lib/planning/queryKeys';
import { techCardErrorText } from '@/lib/planning/techCards';
import { toast } from '@/lib/toast';

// ── Типы ответа ──────────────────────────────────────────────────────────────

/** Что со строкой плана: назначена / без техкарты / пар / культура не назначена. */
export type RowState = 'assigned' | 'unassigned' | 'fallow' | 'no_crop';

export interface RowTechCard {
  id: string;
  name: string;
  status: string;
  predecessor_crop_id: string | null;
  predecessor_name: string | null;
  assigned_at: string | null;
  changed_at: string | null;
  /** Карта заведена ровно под предшественник строки (а не «общая по культуре»). */
  exact_predecessor: boolean;
}

export interface PlanRow {
  key: string;
  crop_id: string | null;
  crop_name: string | null;
  predecessor_crop_id: string | null;
  predecessor_name: string | null;
  field_state_id: string | null;
  field_state_name: string | null;
  state: RowState;
  fields_count: number;
  area_ha: number;
  yield_c_ha: number | null;
  gross_t: number | null;
  price_per_t: number | null;
  revenue: number | null;
  tech_card: RowTechCard | null;
  cost_per_ha: number | null;
  cost_total: number | null;
  cost_by_kind: Partial<Record<'materials' | 'fuel' | 'labor' | 'outsource', number>>;
  margin: number | null;
  margin_per_ha: number | null;
  margin_pct: number | null;
  /**
   * НЗП (ADR-070) — в карте строки есть осенние работы под этот урожай,
   * выполненные в прошлом календарном году. Бейдж «НЗП» на строке.
   * Опциональны в типе (бэк отдаёт всегда) — чтобы старые фикстуры собирались.
   */
  is_wip?: boolean;
  /** Деньги НЗП-части. При подаче «по календарю» уже вычтены из затрат строки. */
  wip_cost_per_ha?: number | null;
  wip_cost_total?: number | null;
  wip_cost_by_kind?: Partial<Record<'materials' | 'fuel' | 'labor' | 'outsource', number>>;
  /** Чего не хватило для честного числа — показываем текстом, не прячем. */
  gaps: string[];
}

/** Подача экономики (ADR-070): «по урожаю» (default) или «по календарю сезона». */
export type SummaryBasis = 'harvest' | 'cash';

/**
 * Статусы исходящего НЗП: `ok` — по выбранному плану X+1; `next_season_draft` —
 * по черновику (он ещё меняется, «деньги года» меняются с ним);
 * `next_season_not_planned` — плана X+1 нет; `not_requested` — не считалось
 * (исходящее дорогое, считается только при `basis=cash` или `include_wip=1`).
 */
export type WipOutgoingStatus = 'ok' | 'next_season_draft' | 'next_season_not_planned' | 'not_requested';

/** Чей план дал исходящее НЗП — вариант следующего сезона. */
export interface WipSource {
  scenario_id: string;
  name: string;
  season_year: number;
  is_selected: boolean;
}

/** НЗП варианта — мостик подач: `cash = by_harvest − incoming + outgoing`. */
export interface WipTotals {
  /** Осенние работы прошлого календарного года под ЭТОТ урожай, ₽. */
  incoming: number;
  /** Осень этого года под СЛЕДУЮЩИЙ урожай. `null` — см. `outgoing_status`. */
  outgoing: number | null;
  cash_in_season: number;
  outgoing_status: WipOutgoingStatus;
  outgoing_source: WipSource | null;
}

export interface Coverage {
  rows_total: number;
  rows_assigned: number;
  total_ha: number;
  assigned_ha: number;
  unassigned_ha: number;
  pct: number | null;
  arable_ha: number;
  fallow_ha: number;
  no_crop_ha: number;
}

/** Почему назначенная строка не попала в расчёт — словами бэкенда. */
export interface NotInCalcReason {
  reason: string;
  rows: number;
  area_ha: number;
}

export interface VariantTotals {
  rows_calculated: number;
  area_ha: number;
  gross_t: number;
  /** `null` — деньги реализации скрыты правами (см. `prices_visible`). */
  revenue: number | null;
  cost: number;
  cost_per_ha: number | null;
  margin: number | null;
  margin_per_ha: number | null;
  margin_pct: number | null;
  /**
   * Почему маржа не показана. При подаче «по календарю» всегда
   * `margin_is_harvest_basis_only`: выручка урожая обязана встречаться со
   * ВСЕМИ затратами урожая, а не с деньгами календарного года.
   * Опциональны в типе (бэк отдаёт всегда) — чтобы старые фикстуры собирались.
   */
  margin_reason?: string | null;
  wip?: WipTotals | null;
  /**
   * Видит ли пользователь цены реализации. Без права на бюджетные справочники
   * (агроном, мониторинг-леди) бэк не отдаёт ни цену тонны, ни выручку, ни
   * маржу — экран обязан сказать это словами, а не рисовать прочерки молча.
   */
  prices_visible: boolean;
  not_in_calc: {
    rows: number;
    area_ha: number;
    gross_t: number;
    unassigned_ha: number;
    no_crop_ha: number;
    fallow_ha: number;
    /** Назначенные строки, у которых не посчиталась выручка. */
    incomplete_rows: number;
    incomplete_ha: number;
    reasons: NotInCalcReason[];
  };
}

export interface CalcChange {
  kind: 'catalog' | 'tech_card';
  catalog_kind?: string;
  catalog_title?: string;
  what: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  changed_at: string;
  area_ha?: number;
}

export interface CalculationState {
  calculated_at: string | null;
  versions: Record<string, number>;
  catalogs: { catalog_kind: string; title: string; version: number; status: string }[];
  is_stale: boolean;
  changes: CalcChange[];
}

export interface VariantSummary {
  basis?: SummaryBasis;
  scenario: {
    id: string;
    name: string;
    status: string;
    is_selected: boolean;
    farm_id: string;
    farm_name: string;
    season_id: string;
    season_year: number;
  };
  rows: PlanRow[];
  coverage: Coverage;
  totals: VariantTotals;
  /**
   * Светофор против цели СВОЕГО уровня (ADR-069): вариант хозяйства — против
   * farm-цели. `null` — целей не задано или деньги скрыты правами. Факт —
   * эти же `totals` (подача «по урожаю», ADR-070 D3).
   */
  targets: VariantTargets | null;
  calculation: CalculationState;
}

// ── Ключи и чтение ───────────────────────────────────────────────────────────

// Ключи делят два модуля (назначение живёт в техкартах, чтение — здесь),
// поэтому лежат в общем `queryKeys.ts`; тут переэкспорт для экранов.
export { planVariantKeys, techCardKeys };

export function useVariantSummary(
  scenarioId: string | null,
  basis: SummaryBasis = 'harvest',
  /**
   * Исходящее НЗП = полный расчёт плана X+1 (дорого) — бэк считает его только
   * по явному запросу. Просит ТОЛЬКО экран, который показывает блок НЗП;
   * остальные потребители (назначение, сравнение, цели) живут без него.
   */
  includeWip = false,
) {
  return useQuery<VariantSummary, Error>({
    // Подача и запрос НЗП — часть ключа: это разные ответы бэка. Ключ остаётся
    // ПОД `planVariantKeys.summary`, чтобы существующая инвалидация по
    // префиксу продолжала накрывать все варианты.
    queryKey: [...planVariantKeys.summary(scenarioId), basis, includeWip ? 'wip' : 'no-wip'],
    queryFn: () =>
      api.get<VariantSummary>(
        `/api/v1/planning/scenarios/${scenarioId}/summary?basis=${basis}${includeWip ? '&include_wip=1' : ''}`,
      ),
    enabled: !!scenarioId,
    staleTime: 15_000,
  });
}

/** Помесячный свод НЗП (аналог листа «Свод ТК (БПУ) НЗП»). */
export interface WipMonth {
  /** `YYYY-MM` начала агроокна; `null` — НЗП задано вручную у строки без окна. */
  month: string | null;
  materials: number;
  fuel: number;
  labor: number;
  outsource: number;
  total: number;
}

export interface WipReport {
  season_year: number;
  next_season_year: number | null;
  incoming: { total: number; months: WipMonth[] };
  outgoing: { total: number; months: WipMonth[] } | null;
  outgoing_status: WipOutgoingStatus;
  outgoing_source: WipSource | null;
}

export function useVariantWipReport(scenarioId: string | null, enabled = true) {
  return useQuery<WipReport, Error>({
    queryKey: [...planVariantKeys.summary(scenarioId), 'wip'],
    queryFn: () => api.get<WipReport>(`/api/v1/planning/scenarios/${scenarioId}/summary/wip`),
    enabled: !!scenarioId && enabled,
    staleTime: 15_000,
  });
}

/** «сентябрь 2026» из ключа `YYYY-MM`; без даты — честные слова, не прочерк. */
export function formatWipMonth(month: string | null): string {
  if (!month) return 'без даты (задано вручную)';
  const d = new Date(`${month}-01T00:00:00`);
  if (Number.isNaN(d.getTime())) return month;
  return d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
}

// ── Запись ───────────────────────────────────────────────────────────────────

/** Симметрично `techCards.useInvalidate`: правка назначений трогает оба неймспейса. */
function useInvalidateVariant() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: planVariantKeys.all() });
    void qc.invalidateQueries({ queryKey: techCardKeys.all() });
  };
}

/** «Скопировать назначения из соседнего варианта» — второй вариант за минуты. */
export function useCopyAssignments() {
  const invalidate = useInvalidateVariant();
  return useMutation<{ copied: number }, Error, { targetId: string; sourceId: string; replace?: boolean }>({
    mutationFn: ({ targetId, sourceId, replace }) =>
      api.post<{ copied: number }>(
        `/api/v1/planning/scenarios/${targetId}/tech-cards/copy-from/${sourceId}${replace ? '?replace=true' : ''}`,
        {},
      ),
    onSuccess: (res) => {
      invalidate();
      toast.success(
        res.copied > 0
          ? `Скопировано назначений: ${res.copied}`
          : 'Копировать было нечего — в соседнем варианте назначений нет',
      );
    },
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

/** «Пересчитать вариант» — принять текущую базу справочников за базу расчёта. */
export function useRecalculateVariant() {
  const invalidate = useInvalidateVariant();
  return useMutation<{ calculated_at: string }, Error, { scenarioId: string }>({
    mutationFn: ({ scenarioId }) => api.post<{ calculated_at: string }>(`/api/v1/planning/scenarios/${scenarioId}/recalculate`, {}),
    onSuccess: () => {
      invalidate();
      toast.success('Расчёт пересчитан по текущим бюджетным справочникам');
    },
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

// ── Производные для экранов ──────────────────────────────────────────────────

export interface CropGroup {
  crop_id: string | null;
  title: string;
  rows: PlanRow[];
  area_ha: number;
  share_pct: number | null;
  assigned: number;
  gross_t: number | null;
  revenue: number | null;
  cost_total: number | null;
  cost_per_ha: number | null;
  yield_c_ha: number | null;
  margin: number | null;
  margin_per_ha: number | null;
  margin_pct: number | null;
}

/**
 * Строки, сгруппированные по культуре — так их показывают оба макета:
 * заголовок культуры с площадью и долей, под ним строки по предшественникам.
 * Пар и нераспределённое собираются в одну группу в конце: технологии там нет,
 * а место в структуре они занимают.
 */
export function groupRowsByCrop(rows: PlanRow[], arableHa: number): CropGroup[] {
  const groups = new Map<string, PlanRow[]>();
  for (const row of rows) {
    const key = row.state === 'assigned' || row.state === 'unassigned' ? (row.crop_id ?? 'none') : 'other';
    const list = groups.get(key);
    if (list) list.push(row);
    else groups.set(key, [row]);
  }

  const out: CropGroup[] = [];
  for (const [key, list] of groups) {
    const area = sum(list.map((r) => r.area_ha));
    const gross = sumOrNull(list.map((r) => r.gross_t));
    const revenue = sumOrNull(list.map((r) => r.revenue));
    const cost = sumOrNull(list.map((r) => r.cost_total));
    const costArea = sum(list.filter((r) => r.cost_total !== null).map((r) => r.area_ha));
    const margin = revenue !== null && cost !== null ? revenue - cost : null;
    out.push({
      crop_id: key === 'other' || key === 'none' ? null : key,
      title: key === 'other' ? 'Пар и нераспределённое' : (list[0]?.crop_name ?? 'Культура не назначена'),
      rows: list,
      area_ha: area,
      share_pct: arableHa > 0 ? (area / arableHa) * 100 : null,
      assigned: list.filter((r) => r.state === 'assigned').length,
      gross_t: gross,
      revenue,
      cost_total: cost,
      cost_per_ha: cost !== null && costArea > 0 ? cost / costArea : null,
      yield_c_ha: weightedYield(list),
      margin,
      margin_per_ha: margin !== null && costArea > 0 ? margin / costArea : null,
      margin_pct: margin !== null && revenue ? (margin / revenue) * 100 : null,
    });
  }
  // Культуры по убыванию площади — как в макете (пшеница первой), «пар и
  // нераспределённое» всегда последним.
  return out.sort((a, b) => {
    const aOther = a.title === 'Пар и нераспределённое' ? 1 : 0;
    const bOther = b.title === 'Пар и нераспределённое' ? 1 : 0;
    return aOther - bOther || b.area_ha - a.area_ha;
  });
}

function sum(values: number[]): number {
  return values.reduce((acc, v) => acc + v, 0);
}

function sumOrNull(values: (number | null)[]): number | null {
  const present = values.filter((v): v is number => v !== null);
  return present.length === 0 ? null : sum(present);
}

function weightedYield(rows: PlanRow[]): number | null {
  const withYield = rows.filter((r) => r.yield_c_ha !== null && r.area_ha > 0);
  if (withYield.length === 0) return null;
  const area = sum(withYield.map((r) => r.area_ha));
  return area > 0 ? sum(withYield.map((r) => (r.yield_c_ha as number) * r.area_ha)) / area : null;
}

export interface CardOption {
  id: string;
  name: string;
  costPerHa: number | null;
  /** Сумма на площадь строки — макет показывает и ₽/га, и «во что это встанет». */
  costTotal: number | null;
  deltaPerHa: number | null;
  predecessorName: string | null;
  exactPredecessor: boolean;
  operationsCount: number | null;
  isCurrent: boolean;
  /** AK-770: чья карта — своя, компании или соседнего хозяйства. */
  scope: 'farm' | 'company' | 'other_farm';
  /** Имя хозяйства-владельца (у карты уровня компании его нет). */
  farmName: string | null;
  /** Карта заведена на ДРУГУЮ запись той же культуры («Подсолнечник» под «масличным»). */
  familyCrop: boolean;
  /** Культура самой карты — показывается, когда она не совпадает со строкой. */
  cropName: string | null;
  /** Назначается только копией в своё хозяйство (`…/tech-cards/adopt`). */
  adoptRequired: boolean;
}

/**
 * Подбор варианта для выделенной строки: ₽/га И сумма на площадь строки,
 * плюс дельта к назначенному сейчас. Годятся варианты той же культуры —
 * заведённые под этот предшественник и общие (без предшественника).
 *
 * AK-770: подбор считает сервер (`…/tech-cards/candidates`) — он знает и про
 * карты уровня компании, и про «ту же культуру, записанную иначе». Карты,
 * пришедшие с пометкой `crop_match`, по культуре здесь уже НЕ фильтруются:
 * иначе клиентское строгое равенство `crop_id` второй раз отрезало бы ровно
 * то, ради чего подбор ходил на сервер (16 карт «Подсолнечника» под строкой
 * «Подсолнечник масличный»). Для старых вызовов без пометки правило прежнее.
 */
export function cardOptionsForRow(
  cards: {
    id: string;
    name: string;
    crop_id: string;
    predecessor_crop_id: string | null;
    /** Множество предшественников (ADR-071 D2); одиночное поле — производное. */
    predecessor_crop_ids?: string[];
    archived_at: string | null;
    cost?: { per_ha: number } | null;
    operations?: unknown[];
    scope?: 'farm' | 'company' | 'other_farm';
    farm_name?: string | null;
    crop_match?: 'exact' | 'family';
    crop_name?: string | null;
    adopt_required?: boolean;
  }[],
  row: PlanRow,
  predecessorNames: Record<string, string>,
): CardOption[] {
  if (!row.crop_id) return [];
  const current = row.tech_card?.id ?? null;
  const currentCost = row.cost_per_ha;
  return cards
    .filter((c) => (c.crop_match ? true : c.crop_id === row.crop_id) && !c.archived_at)
    .map((c) => {
      const perHa = c.cost?.per_ha ?? null;
      // Предшественники — множество (ADR-071 D2): точное попадание = вход
      // предшественника строки в непустое множество карты.
      const preds = c.predecessor_crop_ids ?? (c.predecessor_crop_id ? [c.predecessor_crop_id] : []);
      const exact = preds.length > 0 && !!row.predecessor_crop_id && preds.includes(row.predecessor_crop_id);
      const predecessorName = preds
        .map((id) => predecessorNames[id])
        .filter(Boolean)
        .join(', ');
      return {
        id: c.id,
        name: c.name,
        costPerHa: perHa,
        costTotal: perHa === null ? null : perHa * row.area_ha,
        deltaPerHa: perHa === null || currentCost === null || c.id === current ? null : perHa - currentCost,
        predecessorName: predecessorName || null,
        exactPredecessor: exact,
        operationsCount: Array.isArray(c.operations) && c.operations.length > 0 ? c.operations.length : null,
        isCurrent: c.id === current,
        scope: c.scope ?? 'farm',
        farmName: c.farm_name ?? null,
        familyCrop: c.crop_match === 'family',
        cropName: c.crop_name ?? null,
        adoptRequired: c.adopt_required ?? false,
      };
    })
    .sort((a, b) => {
      // Карта на ТУ ЖЕ культуру всегда выше «той же культуры, записанной иначе»
      // — иначе подбор предлагал бы соседнюю запись справочника вперёд точной.
      if (a.familyCrop !== b.familyCrop) return a.familyCrop ? 1 : -1;
      // Сначала карта, заведённая ровно под этот предшественник — она и есть
      // «подходит»; дальше по цене, чтобы дешёвое было видно без прокрутки.
      if (a.exactPredecessor !== b.exactPredecessor) return a.exactPredecessor ? -1 : 1;
      // Готовая к назначению карта — выше той, которую сначала надо взять
      // копией: дешевизна не повод предлагать лишний шаг первым.
      if (a.adoptRequired !== b.adoptRequired) return a.adoptRequired ? 1 : -1;
      return (a.costPerHa ?? Number.POSITIVE_INFINITY) - (b.costPerHa ?? Number.POSITIVE_INFINITY);
    });
}

/**
 * Название культуры в дательном падеже — «по кукурузе», «по озимой пшенице».
 *
 * Названия культур приходят из справочника в именительном («Кукуруза на
 * зерно»), а строка плана читается предлогом «по». Просто понизить регистр
 * мало: «по кукуруза на зерно» — это не язык агронома, а машинный подстрочник.
 *
 * Склоняем ТОЛЬКО главную часть — слова до предлога («на зерно», «для
 * силоса» остаются как есть), прилагательные и существительные по своим
 * окончаниям. Несколько неправильных названий держим списком: «лён» и «овёс»
 * по общему правилу дали бы «лёну» и «овёсу».
 */
const DATIVE_EXCEPTIONS: Record<string, string> = {
  лён: 'льну',
  лен: 'льну',
  овёс: 'овсу',
  овес: 'овсу',
  рожь: 'ржи',
};

const TAIL_PREPOSITIONS = new Set(['на', 'для', 'с', 'из', 'по', 'под']);

function wordToDative(word: string): string {
  const lower = word.toLowerCase();
  const exception = DATIVE_EXCEPTIONS[lower];
  if (exception) return exception;
  // Прилагательное: озимая → озимой, яровой → яровому, ранний → раннему.
  if (lower.endsWith('ая')) return `${lower.slice(0, -2)}ой`;
  if (lower.endsWith('яя')) return `${lower.slice(0, -2)}ей`;
  if (lower.endsWith('ый') || lower.endsWith('ой')) return `${lower.slice(0, -2)}ому`;
  if (lower.endsWith('ий')) return `${lower.slice(0, -2)}ему`;
  // Существительное: пшеница → пшенице, соя → сое, просо → просу,
  // ячмень → ячменю, подсолнечник → подсолнечнику.
  if (lower.endsWith('а') || lower.endsWith('я')) return `${lower.slice(0, -1)}е`;
  if (lower.endsWith('о') || lower.endsWith('е')) return `${lower.slice(0, -1)}у`;
  if (lower.endsWith('ь') || lower.endsWith('й')) return `${lower.slice(0, -1)}ю`;
  return `${lower}у`;
}

export function cropToDative(name: string): string {
  const words = name.trim().split(/\s+/);
  const out: string[] = [];
  let inTail = false;
  for (const word of words) {
    if (TAIL_PREPOSITIONS.has(word.toLowerCase())) inTail = true;
    out.push(inTail ? word.toLowerCase() : wordToDative(word));
  }
  return out.join(' ');
}

/** Название строки плана языком агронома: «по кукурузе», «повторная, по озимой пшенице». */
export function rowTitle(row: PlanRow): string {
  if (row.state === 'fallow') return row.field_state_name ?? 'Без культуры';
  if (row.state === 'no_crop') return 'Культура не назначена';
  if (!row.predecessor_name) return 'предшественник неизвестен';
  if (row.predecessor_crop_id && row.predecessor_crop_id === row.crop_id) {
    return `повторная, по ${cropToDative(row.predecessor_name)}`;
  }
  return `по ${cropToDative(row.predecessor_name)}`;
}

export const ROW_STATE_TITLES: Record<RowState, string> = {
  assigned: 'назначен',
  unassigned: 'без техкарты',
  fallow: 'без культуры',
  no_crop: 'нет культуры',
};

export const COST_KIND_TITLES: Record<string, string> = {
  materials: 'ТМЦ (семена, удобрения, СЗР)',
  fuel: 'ГСМ',
  labor: 'Оплата труда',
  outsource: 'Наёмные работы и аутсорс',
};

/** Доли видов затрат по всему варианту — карточка «из чего сложились затраты». */
export function costSplit(rows: PlanRow[]): { kind: string; title: string; amount: number; pct: number }[] {
  const totals: Record<string, number> = {};
  for (const row of rows) {
    for (const [kind, amount] of Object.entries(row.cost_by_kind ?? {})) {
      totals[kind] = (totals[kind] ?? 0) + (amount ?? 0);
    }
  }
  const total = Object.values(totals).reduce((a, b) => a + b, 0);
  return Object.entries(totals)
    .filter(([, amount]) => amount > 0)
    .map(([kind, amount]) => ({
      kind,
      title: COST_KIND_TITLES[kind] ?? kind,
      amount,
      pct: total > 0 ? (amount / total) * 100 : 0,
    }))
    .sort((a, b) => b.amount - a.amount);
}

/** Сверка двух вариантов: показатель, оба значения, разница. */
export interface CompareLine {
  label: string;
  left: number | null;
  right: number | null;
  delta: number | null;
  unit: 'money' | 'tons' | 'per_ha' | 'pct';
  /** Рост показателя — это хорошо (маржа) или плохо (затраты). */
  betterWhen: 'higher' | 'lower';
}

export function compareVariants(left: VariantTotals, right: VariantTotals): CompareLine[] {
  const line = (
    label: string,
    pick: (t: VariantTotals) => number | null,
    unit: CompareLine['unit'],
    betterWhen: CompareLine['betterWhen'],
  ): CompareLine => {
    const l = pick(left);
    const r = pick(right);
    return { label, left: l, right: r, delta: l !== null && r !== null ? r - l : null, unit, betterWhen };
  };
  return [
    line('Валовой сбор', (t) => t.gross_t, 'tons', 'higher'),
    line('Выручка', (t) => t.revenue, 'money', 'higher'),
    line('Прямые затраты', (t) => t.cost, 'money', 'lower'),
    line('Маржа', (t) => t.margin, 'money', 'higher'),
    line('Маржа с гектара', (t) => t.margin_per_ha, 'per_ha', 'higher'),
    line('Маржа, %', (t) => t.margin_pct, 'pct', 'higher'),
  ];
}

// ── Форматирование ───────────────────────────────────────────────────────────

const NUM = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const NUM1 = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });

export function formatHa(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${NUM.format(Math.round(value))} га`;
}

export function formatTons(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${NUM.format(Math.round(value))} т`;
}

/** Деньги плана — в миллионах: сотни миллионов цифрами не читаются. */
export function formatMln(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (Math.abs(value) < 1_000_000) return `${NUM.format(Math.round(value))} ₽`;
  return `${NUM1.format(value / 1_000_000)} млн ₽`;
}

export function formatRubPerHa(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${NUM.format(Math.round(value))} ₽/га`;
}

export function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${NUM1.format(value)} %`;
}

export function formatSignedMln(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value) || Math.abs(value) < 1) return 'без изменений';
  const sign = value < 0 ? '−' : '+';
  return `${sign}${formatMln(Math.abs(value))}`;
}

export function formatSignedPerHa(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value) || Math.round(value) === 0) return 'без изменений';
  const sign = value < 0 ? '−' : '+';
  return `${sign}${NUM.format(Math.abs(Math.round(value)))} ₽/га`;
}

export function formatDeltaByUnit(value: number | null, unit: CompareLine['unit']): string {
  if (value === null || !Number.isFinite(value)) return '—';
  switch (unit) {
    case 'tons':
      return `${value < 0 ? '−' : '+'}${NUM.format(Math.abs(Math.round(value)))} т`;
    case 'per_ha':
      return formatSignedPerHa(value);
    case 'pct':
      return `${value < 0 ? '−' : '+'}${NUM1.format(Math.abs(value))} п.п.`;
    default:
      return formatSignedMln(value);
  }
}

export function formatValueByUnit(value: number | null, unit: CompareLine['unit']): string {
  switch (unit) {
    case 'tons':
      return formatTons(value);
    case 'per_ha':
      return formatRubPerHa(value);
    case 'pct':
      return formatPct(value);
    default:
      return formatMln(value);
  }
}

/** «14 093 из 15 360 га» — заголовок покрытия. */
export function coverageHeadline(coverage: Coverage): string {
  return `${NUM.format(Math.round(coverage.assigned_ha))} из ${NUM.format(Math.round(coverage.total_ha))} га`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
